import { Middleware } from '@reduxjs/toolkit'
import { addNotification } from '../slices/uiSlice'

/**
 * Error handling middleware for RTK Query
 * Automatically shows notifications for API errors
 */
export const errorHandlerMiddleware: Middleware = (store) => (next) => (action: unknown) => {
  const typedAction = action as { type: string; payload?: unknown; error?: unknown }
  
  // Check if this is a rejected RTK Query action
  if (typedAction.type.endsWith('/rejected')) {
    const error = typedAction.payload || typedAction.error
    
    // Extract error message
    let errorMessage = 'An unexpected error occurred'
    let errorTitle = 'Error'
    
    if (error && typeof error === 'object' && 'data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
      errorMessage = String(error.data.message)
    } else if (error && typeof error === 'object' && 'message' in error) {
      errorMessage = String(error.message)
    } else if (typeof error === 'string') {
      errorMessage = error
    }
    
    // Determine error type based on status code
    let errorType: 'error' | 'warning' = 'error'
    if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
      errorTitle = 'Authentication Error'
      errorMessage = 'Please log in again'
    } else if (error && typeof error === 'object' && 'status' in error && error.status === 403) {
      errorTitle = 'Permission Error'
      errorMessage = 'You do not have permission to perform this action'
      errorType = 'warning'
    } else if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
      errorTitle = 'Not Found'
      errorMessage = 'The requested resource was not found'
      errorType = 'warning'
    } else if (error && typeof error === 'object' && 'status' in error && error.status === 409) {
      errorTitle = 'Conflict'
      errorMessage = 'The request conflicts with the current state'
      errorType = 'warning'
    } else if (error && typeof error === 'object' && 'status' in error && error.status === 422) {
      errorTitle = 'Validation Error'
      errorType = 'warning'
    } else if (error && typeof error === 'object' && 'status' in error && typeof error.status === 'number' && error.status >= 500) {
      errorTitle = 'Server Error'
      errorMessage = 'A server error occurred. Please try again later.'
    }
    
    // Don't show notifications for certain actions
    const silentActions = [
      'authApi/executeQuery',
      'userApi/executeQuery',
      'cigarApi/executeQuery',
      'humidorApi/executeQuery',
      'sessionApi/executeQuery',
      'brandApi/executeQuery',
      'submissionApi/executeQuery',
      'adminApi/executeQuery',
    ]
    
    const shouldShowNotification = !silentActions.some(silentAction => 
      typedAction.type.includes(silentAction)
    )
    
    if (shouldShowNotification) {
      store.dispatch(addNotification({
        type: errorType,
        title: errorTitle,
        message: errorMessage,
      }))
    }
  }
  
  return next(typedAction)
}

/**
 * Loading state middleware for RTK Query
 * Automatically manages loading states
 */
export const loadingStateMiddleware: Middleware = (store) => (next) => (action: unknown) => {
  const typedAction = action as { type: string }
  const result = next(typedAction)
  
  // Handle pending states
  if (typedAction.type.endsWith('/pending')) {
    const apiName = typedAction.type.split('/')[0]
    store.dispatch({
      type: 'ui/setLoading',
      payload: { key: apiName, loading: true }
    })
  }
  
  // Handle fulfilled/rejected states
  if (typedAction.type.endsWith('/fulfilled') || typedAction.type.endsWith('/rejected')) {
    const apiName = typedAction.type.split('/')[0]
    store.dispatch({
      type: 'ui/setLoading',
      payload: { key: apiName, loading: false }
    })
  }
  
  return result
}
